import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Star, Users, CheckCircle, ExternalLink, Video } from 'lucide-react';

const OnlineClassView = ({ classId, onExit }) => {
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [behaviors, setBehaviors] = useState([]);
  const [isBehaviorDialogOpen, setIsBehaviorDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [behaviorRating, setBehaviorRating] = useState(0);
  const [behaviorNotes, setBehaviorNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [meetOpened, setMeetOpened] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));
  const isTeacher = user?.role === 'teacher' || user?.role === 'admin';

  useEffect(() => {
    loadClassData();
    if (user.role === 'student') {
      joinClass();
    }
  }, [classId]);

  const loadClassData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [classRes, behaviorsRes] = await Promise.all([
        axios.get(`${API}/online-classes/${classId}`, { headers }),
        isTeacher ? axios.get(`${API}/online-classes/${classId}/behaviors`, { headers }) : Promise.resolve({ data: [] })
      ]);

      setClassData(classRes.data);
      setBehaviors(behaviorsRes.data);

      // Load batch students if teacher
      if (isTeacher && classRes.data.batch_id) {
        const batchRes = await axios.get(`${API}/batches/${classRes.data.batch_id}`, { headers });
        const studentIds = batchRes.data.student_ids || [];
        
        // Get online students only
        const usersRes = await axios.get(`${API}/users?role=student`, { headers });
        const onlineStudents = usersRes.data.filter(s => 
          studentIds.includes(s.id) && s.student_mode === 'online'
        );
        setStudents(onlineStudents);
      }
    } catch (error) {
      console.error('Error loading class data:', error);
      toast.error('Failed to load class data');
    } finally {
      setLoading(false);
    }
  };

  const joinClass = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/online-classes/${classId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error joining class:', error);
    }
  };

  const handleOpenMeet = () => {
    if (classData?.meet_link) {
      window.open(classData.meet_link, '_blank', 'noopener,noreferrer');
      setMeetOpened(true);
      toast.success('Google Meet opened in new tab');
    }
  };

  const handleEndClass = async () => {
    if (!window.confirm('Are you sure you want to end this class?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/online-classes/${classId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Class ended successfully');
      onExit();
    } catch (error) {
      console.error('Error ending class:', error);
      toast.error('Failed to end class');
    }
  };

  const openBehaviorDialog = (student) => {
    setSelectedStudent(student);
    // Check if already marked
    const existing = behaviors.find(b => b.student_id === student.id);
    if (existing) {
      setBehaviorRating(existing.behavior_rating);
      setBehaviorNotes(existing.notes || '');
    } else {
      setBehaviorRating(0);
      setBehaviorNotes('');
    }
    setIsBehaviorDialogOpen(true);
  };

  const markBehavior = async () => {
    if (behaviorRating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/online-classes/${classId}/behavior`,
        {
          student_id: selectedStudent.id,
          behavior_rating: behaviorRating,
          notes: behaviorNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Behavior marked successfully');
      setIsBehaviorDialogOpen(false);
      loadClassData();
    } catch (error) {
      console.error('Error marking behavior:', error);
      toast.error('Failed to mark behavior');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  if (!classData) {
    return <div className="text-center py-8">Class not found</div>;
  }

  const getStudentBehavior = (studentId) => {
    return behaviors.find(b => b.student_id === studentId);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={onExit} size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Exit Class
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{classData.batch_name}</h1>
              <p className="text-sm text-gray-600">Online Class with {classData.teacher_name}</p>
            </div>
          </div>
          {isTeacher && (
            <Button onClick={handleEndClass} variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">
              End Class
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Meet Section */}
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-blue-900 to-purple-900 p-8">
          <Card className="max-w-lg w-full p-8 text-center bg-white/95 backdrop-blur shadow-2xl">
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
                <Video className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {meetOpened ? 'Meeting in Progress' : 'Join Google Meet'}
              </h2>
              <p className="text-gray-600">
                {meetOpened 
                  ? 'Your Google Meet session is open in another tab. Return here to manage students and end the class.'
                  : 'Click the button below to join the class in Google Meet. The meeting will open in a new tab.'}
              </p>
            </div>

            <Button 
              onClick={handleOpenMeet}
              className="w-full py-6 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              data-testid="join-meet-button"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              {meetOpened ? 'Rejoin Google Meet' : 'Join Google Meet'}
            </Button>

            <p className="text-xs text-gray-500 mt-4">
              Meeting Link: <a href={classData.meet_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{classData.meet_link}</a>
            </p>
          </Card>
        </div>

        {/* Sidebar for Teacher */}
        {isTeacher && (
          <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Students ({students.length})
              </h2>
              <p className="text-xs text-gray-600 mt-1">Mark behavior & progress</p>
            </div>

            <div className="p-4 space-y-3">
              {students.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No online students in this batch</p>
              ) : (
                students.map(student => {
                  const behavior = getStudentBehavior(student.id);
                  const isPresent = classData.students_present?.includes(student.id);
                  
                  return (
                    <Card key={student.id} className={`p-3 ${
                      isPresent ? 'border-green-200 bg-green-50' : 'border-gray-200'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">{student.name}</p>
                          <p className="text-xs text-gray-600">{student.email}</p>
                        </div>
                        {isPresent && (
                          <CheckCircle className="w-4 h-4 text-green-600" title="Present" />
                        )}
                      </div>

                      {behavior ? (
                        <div className="bg-white rounded p-2 mb-2">
                          <div className="flex items-center gap-1 mb-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 ${
                                  i < behavior.behavior_rating
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          {behavior.notes && (
                            <p className="text-xs text-gray-600">{behavior.notes}</p>
                          )}
                        </div>
                      ) : null}

                      <Button
                        onClick={() => openBehaviorDialog(student)}
                        size="sm"
                        variant="outline"
                        className="w-full text-xs"
                      >
                        {behavior ? 'Update' : 'Mark'} Behavior
                      </Button>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Behavior Dialog */}
      <Dialog open={isBehaviorDialogOpen} onOpenChange={setIsBehaviorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Behavior - {selectedStudent?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Behavior Rating</Label>
              <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4, 5].map(rating => (
                  <button
                    key={rating}
                    onClick={() => setBehaviorRating(rating)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      behaviorRating === rating
                        ? 'border-yellow-400 bg-yellow-50'
                        : 'border-gray-200 hover:border-yellow-300'
                    }`}
                  >
                    <Star
                      className={`w-8 h-8 ${
                        behaviorRating >= rating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-600 text-center mt-2">
                {behaviorRating === 1 && 'Needs Improvement'}
                {behaviorRating === 2 && 'Below Average'}
                {behaviorRating === 3 && 'Average'}
                {behaviorRating === 4 && 'Good'}
                {behaviorRating === 5 && 'Excellent'}
              </p>
            </div>

            <div>
              <Label htmlFor="behavior_notes">Notes (Optional)</Label>
              <Textarea
                id="behavior_notes"
                placeholder="Add observations about student behavior, participation, understanding..."
                value={behaviorNotes}
                onChange={(e) => setBehaviorNotes(e.target.value)}
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setIsBehaviorDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button onClick={markBehavior} className="flex-1 bg-blue-600">
                Save Behavior
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnlineClassView;
